import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import { advertisementsAPI, paymentAdAPI, getBackendURL } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Edit, Trash2, Image as ImageIcon, ExternalLink, CheckCircle, Upload, Film, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CHUNK_SIZE = 512 * 1024;
const resolveMediaUrl = (u) => (u && u.startsWith('/') ? `${getBackendURL()}${u}` : u);

const AdPreviewThumb = ({ ad, className }) => (
  ad.media_type === 'video' ? (
    <video src={resolveMediaUrl(ad.image_url)} muted playsInline className={className} />
  ) : (
    <img
      src={resolveMediaUrl(ad.image_url)}
      alt={ad.title}
      className={className}
      onError={(e) => e.target.src = 'https://via.placeholder.com/150x100?text=Erro'}
    />
  )
);

const SortableAdRow = ({ ad, idx, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ad.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <tr ref={setNodeRef} style={style} className={`border-b hover:bg-gray-50 ${isDragging ? 'bg-purple-50 shadow-lg' : ''}`} data-testid={`ad-row-${ad.id}`}>
      <td className="p-3 w-8">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="touch-none cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
          title="Arraste para reordenar"
          data-testid={`ad-drag-handle-${ad.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="p-3">
        <AdPreviewThumb ad={ad} className="w-24 h-16 object-cover rounded border" />
      </td>
      <td className="p-3 font-medium">
        <div className="flex items-center gap-1">
          {ad.media_type === 'video' && <Film className="h-3 w-3 text-purple-600" />}
          {ad.title}
        </div>
      </td>
      <td className="p-3 text-center">{idx + 1}º</td>
      <td className="p-3 text-xs">
        {ad.link_url ? (
          <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[200px]">
            {ad.link_url}
          </a>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="p-3 text-center">
        <Badge variant={ad.active ? 'default' : 'secondary'}>
          {ad.active ? 'Ativo' : 'Inativo'}
        </Badge>
      </td>
      <td className="p-3">
        <div className="flex gap-1 justify-center">
          <Button onClick={() => onEdit(ad)} variant="outline" size="sm" className="h-7 text-xs">
            <Edit className="mr-1 h-3 w-3" />
            Editar
          </Button>
          <Button onClick={() => onDelete(ad.id)} variant="destructive" size="sm" className="h-7 text-xs">
            <Trash2 className="mr-1 h-3 w-3" />
            Excluir
          </Button>
        </div>
      </td>
    </tr>
  );
};

const SortableAdCard = ({ ad, idx, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ad.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <Card ref={setNodeRef} style={style} className={`${!ad.active ? 'opacity-60' : ''} ${isDragging ? 'shadow-2xl ring-2 ring-purple-400' : ''}`} data-testid={`ad-card-${ad.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <button
              {...attributes}
              {...listeners}
              type="button"
              className="touch-none cursor-grab active:cursor-grabbing p-1 -ml-1 rounded hover:bg-gray-100 text-gray-400"
              title="Arraste para reordenar"
              data-testid={`ad-card-drag-handle-${ad.id}`}
            >
              <GripVertical className="h-4 w-4" />
            </button>
            {ad.media_type === 'video' ? <Film className="h-4 w-4 text-purple-600" /> : <ImageIcon className="h-4 w-4 text-purple-600" />}
            <h3 className="font-bold text-sm">{ad.title}</h3>
          </div>
          <Badge variant={ad.active ? 'default' : 'secondary'} className="text-xs">
            {ad.active ? 'Ativo' : 'Inativo'}
          </Badge>
        </div>

        <AdPreviewThumb ad={ad} className="w-full h-32 object-cover rounded border" />

        <div className="text-xs text-gray-600 space-y-1">
          <p>Posição: {idx + 1}º</p>
          {ad.link_url && (
            <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 truncate block">
              {ad.link_url}
            </a>
          )}
        </div>

        <div className="flex gap-2">
          <Button onClick={() => onEdit(ad)} variant="outline" className="flex-1" size="sm">
            <Edit className="mr-1 h-3 w-3" />
            Editar
          </Button>
          <Button onClick={() => onDelete(ad.id)} variant="destructive" className="flex-1" size="sm">
            <Trash2 className="mr-1 h-3 w-3" />
            Excluir
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdvertisementsManagement = () => {
  const [ads, setAds] = useState([]);
  const [paymentAd, setPaymentAd] = useState(null); // NOVO
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    media_type: 'image',
    link_url: '',
    order: 0,
    active: true
  });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [paymentAdForm, setPaymentAdForm] = useState({ // NOVO
    image_url: '',
    media_type: 'image',
    link_url: '',
    active: false
  });
  const [paymentUploadProgress, setPaymentUploadProgress] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAdId, setDeletingAdId] = useState(null);
  const [uploadingPaymentAd, setUploadingPaymentAd] = useState(false); // NOVO

  useEffect(() => {
    loadAds();
    loadPaymentAd(); // NOVO
  }, []);

  const loadPaymentAd = async () => {
    try {
      const response = await paymentAdAPI.get();
      setPaymentAd(response.data);
      if (response.data) {
        setPaymentAdForm({
          image_url: response.data.image_url,
          media_type: response.data.media_type || 'image',
          link_url: response.data.link_url || '',
          active: response.data.active
        });
      }
    } catch (error) {
      console.log('Nenhum anúncio de confirmação configurado');
    }
  };

  const loadAds = async () => {
    try {
      const response = await advertisementsAPI.listAll();
      setAds(response.data);
    } catch (error) {
      toast.error('Erro ao carregar anúncios');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (ad = null) => {
    if (ad) {
      setEditingAd(ad);
      setFormData({
        title: ad.title,
        image_url: ad.image_url,
        media_type: ad.media_type || 'image',
        link_url: ad.link_url || '',
        order: ad.order,
        active: ad.active
      });
    } else {
      setEditingAd(null);
      setFormData({ title: '', image_url: '', media_type: 'image', link_url: '', order: 0, active: true });
    }
    setUploadProgress('');
    setDialogOpen(true);
  };

  const validateMediaFile = (file) => {
    const isVideo = file.type === 'video/mp4';
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) {
      toast.error('Formato não suportado. Use imagem (PNG/JPG/WebP/GIF) ou vídeo MP4.');
      return false;
    }
    const maxMB = isVideo ? 50 : 10;
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`Arquivo muito grande. Máximo: ${maxMB}MB para ${isVideo ? 'vídeo' : 'imagem'}.`);
      return false;
    }
    return true;
  };

  const uploadMediaChunks = async (file, onProgress) => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      onProgress(`Enviando: ${Math.round(((i + 1) / total) * 100)}%`);
      const slice = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.split(',')[1]);
        r.onerror = reject;
        r.readAsDataURL(slice);
      });
      await advertisementsAPI.uploadChunk({
        upload_id: uploadId, chunk_index: i, total_chunks: total,
        data_b64: b64, content_type: file.type
      });
    }
    const done = await advertisementsAPI.uploadComplete({ upload_id: uploadId });
    return done.data;
  };

  const handleMediaSelect = async (file) => {
    if (!file || !validateMediaFile(file)) return;
    setUploadingMedia(true);
    try {
      const done = await uploadMediaChunks(file, setUploadProgress);
      setFormData(prev => ({ ...prev, image_url: done.media_url, media_type: done.media_type }));
      toast.success(done.media_type === 'video' ? 'Vídeo enviado!' : 'Imagem enviada!');
    } catch (error) {
      toast.error('Erro no upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingMedia(false);
      setUploadProgress('');
    }
  };

  const handlePaymentMediaSelect = async (file) => {
    if (!file || !validateMediaFile(file)) return;
    setUploadingPaymentAd(true);
    try {
      const done = await uploadMediaChunks(file, setPaymentUploadProgress);
      setPaymentAdForm(prev => ({ ...prev, image_url: done.media_url, media_type: done.media_type }));
      toast.success(done.media_type === 'video' ? 'Vídeo enviado!' : 'Imagem enviada!');
    } catch (error) {
      toast.error('Erro no upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingPaymentAd(false);
      setPaymentUploadProgress('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.image_url) {
      toast.error('Envie uma imagem ou vídeo para o anúncio');
      return;
    }

    const data = {
      title: formData.title,
      image_url: formData.image_url,
      media_type: formData.media_type,
      link_url: formData.link_url || null,
      order: editingAd ? editingAd.order : ads.length,
      active: formData.active
    };

    try {
      if (editingAd) {
        await advertisementsAPI.update(editingAd.id, data);
        toast.success('Anúncio atualizado!');
      } else {
        await advertisementsAPI.create(data);
        toast.success('Anúncio criado!');
      }
      setDialogOpen(false);
      loadAds();
    } catch (error) {
      toast.error('Erro ao salvar anúncio');
    }
  };

  const handleSavePaymentAd = async () => {
    if (!paymentAdForm.image_url) {
      toast.error('Envie uma imagem ou vídeo para o anúncio');
      return;
    }
    
    try {
      await paymentAdAPI.update({
        image_url: paymentAdForm.image_url,
        media_type: paymentAdForm.media_type,
        link_url: paymentAdForm.link_url || null,
        active: paymentAdForm.active
      });
      toast.success('Anúncio de confirmação salvo!');
      loadPaymentAd();
    } catch (error) {
      toast.error('Erro ao salvar');
      console.error('Erro:', error);
    }
  };


  const handleDelete = (id) => {
    setDeletingAdId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await advertisementsAPI.delete(deletingAdId);
      toast.success('Anúncio excluído!');
      setShowDeleteConfirm(false);
      setDeletingAdId(null);
      loadAds();
    } catch (error) {
      toast.error('Erro ao excluir');
      setShowDeleteConfirm(false);
    }
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = ads.findIndex(a => a.id === active.id);
    const newIdx = ads.findIndex(a => a.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(ads, oldIdx, newIdx);
    setAds(reordered);
    try {
      await advertisementsAPI.reorder(reordered.map(a => a.id));
      toast.success('Ordem atualizada!');
    } catch (error) {
      toast.error('Erro ao salvar a nova ordem');
      loadAds();
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="ads-management">
      <div>
        <h2 className="text-2xl font-bold">Gerenciar Anúncios</h2>
        <p className="text-gray-600 text-sm">Configure anúncios do carrossel e confirmação</p>
      </div>

      <Tabs defaultValue="carousel" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="carousel">
            <ImageIcon className="w-4 h-4 mr-2" />
            Carrossel
          </TabsTrigger>
          <TabsTrigger value="payment">
            <CheckCircle className="w-4 h-4 mr-2" />
            Confirmação Pagamento
          </TabsTrigger>
        </TabsList>

        {/* Aba Carrossel */}
        <TabsContent value="carousel" className="mt-6 space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-gray-600">Carrossel da página inicial (1152x768px) — arraste ⠿ para reordenar</p>
            <Button onClick={() => handleOpenDialog()} className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Novo Anúncio
            </Button>
          </div>
      <div className="hidden md:block">
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="p-3 w-8"></th>
                    <th className="text-left p-3 font-semibold">Preview</th>
                    <th className="text-left p-3 font-semibold">Título</th>
                    <th className="text-center p-3 font-semibold">Posição</th>
                    <th className="text-left p-3 font-semibold">Link</th>
                    <th className="text-center p-3 font-semibold">Status</th>
                    <th className="text-center p-3 font-semibold">Ações</th>
                  </tr>
                </thead>
                <SortableContext items={ads.map(a => a.id)} strategy={verticalListSortingStrategy}>
                  <tbody>
                    {ads.map((ad, idx) => (
                      <SortableAdRow key={ad.id} ad={ad} idx={idx} onEdit={handleOpenDialog} onDelete={handleDelete} />
                    ))}
                  </tbody>
                </SortableContext>
              </table>
            </div>
          </CardContent>
        </Card>
        </DndContext>
      </div>

      {/* Cards para Mobile */}
      <div className="md:hidden space-y-3">
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ads.map(a => a.id)} strategy={verticalListSortingStrategy}>
            {ads.map((ad, idx) => (
              <SortableAdCard key={ad.id} ad={ad} idx={idx} onEdit={handleOpenDialog} onDelete={handleDelete} />
            ))}
          </SortableContext>
        </DndContext>
      </div>
        </TabsContent>

        {/* Aba Confirmação Pagamento */}
        <TabsContent value="payment" className="mt-6">
          <Card className="border-2 border-green-300">
            <CardHeader>
              <CardTitle className="text-lg">✅ Anúncio de Confirmação de Pagamento</CardTitle>
              <p className="text-sm text-gray-600">Mídia exibida quando o pagamento é confirmado (1920×1080px)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload da Mídia */}
              <div>
                <Label>Imagem ou Vídeo MP4 (1920×1080px)</Label>
                <label
                  className={`mt-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${uploadingPaymentAd ? 'border-green-400 bg-green-50' : 'border-green-300 hover:border-green-500 hover:bg-green-50'}`}
                  data-testid="payment-ad-upload-area"
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,video/mp4"
                    className="hidden"
                    disabled={uploadingPaymentAd}
                    onChange={(e) => handlePaymentMediaSelect(e.target.files?.[0])}
                    data-testid="payment-ad-file-input"
                  />
                  {uploadingPaymentAd ? (
                    <>
                      <FoxSpinnerInline size="sm" />
                      <span className="text-sm text-green-700 font-medium">{paymentUploadProgress}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-green-600" />
                      <span className="text-sm text-gray-700 font-medium">
                        {paymentAdForm.image_url ? 'Clique para substituir' : 'Clique para enviar'}
                      </span>
                      <span className="text-xs text-gray-500">Imagem até 10MB · Vídeo MP4 até 50MB</span>
                    </>
                  )}
                </label>
              </div>

              {/* Preview */}
              {paymentAdForm.image_url && !uploadingPaymentAd && (
                <div>
                  <Label>Preview</Label>
                  {paymentAdForm.media_type === 'video' ? (
                    <video
                      src={resolveMediaUrl(paymentAdForm.image_url)}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full max-w-2xl rounded border"
                      data-testid="payment-ad-preview-video"
                    />
                  ) : (
                    <img
                      src={resolveMediaUrl(paymentAdForm.image_url)}
                      alt="Preview"
                      className="w-full max-w-2xl rounded border"
                      data-testid="payment-ad-preview-image"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/1920x1080?text=Erro+ao+Carregar';
                      }}
                    />
                  )}
                </div>
              )}

              {/* Link */}
              <div>
                <Label>Link de Redirecionamento (opcional)</Label>
                <Input
                  type="url"
                  placeholder="https://exemplo.com"
                  value={paymentAdForm.link_url}
                  onChange={(e) => setPaymentAdForm({...paymentAdForm, link_url: e.target.value})}
                />
              </div>

              {/* Ativo */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={paymentAdForm.active}
                  onCheckedChange={(checked) => setPaymentAdForm({...paymentAdForm, active: checked})}
                />
                <Label>Exibir na confirmação de pagamento</Label>
                <Badge variant={paymentAdForm.active ? 'default' : 'secondary'}>
                  {paymentAdForm.active ? 'ATIVO' : 'INATIVO'}
                </Badge>
              </div>

              {/* Botão Salvar */}
              <Button onClick={handleSavePaymentAd} className="w-full bg-green-600" disabled={uploadingPaymentAd} data-testid="payment-ad-save-button">
                Salvar Anúncio
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAd ? 'Editar Anúncio' : 'Novo Anúncio'}</DialogTitle>
            <DialogDescription>Envie uma imagem ou vídeo MP4 para o carrossel da página inicial.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Imagem ou Vídeo MP4 (1152x768px)</Label>
              <label
                className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${uploadingMedia ? 'border-purple-400 bg-purple-50' : 'border-purple-300 hover:border-purple-500 hover:bg-purple-50'}`}
                data-testid="ad-media-upload-area"
              >
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,video/mp4"
                  className="hidden"
                  disabled={uploadingMedia}
                  onChange={(e) => handleMediaSelect(e.target.files?.[0])}
                  data-testid="ad-media-file-input"
                />
                {uploadingMedia ? (
                  <>
                    <FoxSpinnerInline size="sm" />
                    <span className="text-sm text-purple-700 font-medium">{uploadProgress}</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-purple-500" />
                    <span className="text-sm text-gray-700 font-medium">
                      {formData.image_url ? 'Clique para substituir' : 'Clique para enviar'}
                    </span>
                    <span className="text-xs text-gray-500">Imagem até 10MB · Vídeo MP4 até 50MB</span>
                  </>
                )}
              </label>

              {/* Preview da Mídia */}
              {formData.image_url && !uploadingMedia && (
                <div className="mt-3 border-2 border-dashed border-purple-300 rounded-lg p-2">
                  <p className="text-xs text-gray-600 mb-2">Preview:</p>
                  {formData.media_type === 'video' ? (
                    <video
                      src={resolveMediaUrl(formData.image_url)}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-auto max-h-64 object-contain rounded"
                      data-testid="ad-media-preview-video"
                    />
                  ) : (
                    <img
                      src={resolveMediaUrl(formData.image_url)}
                      alt="Preview"
                      className="w-full h-auto max-h-64 object-contain rounded"
                      data-testid="ad-media-preview-image"
                    />
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Link de Destino (opcional)</Label>
              <Input
                type="url"
                placeholder="https://exemplo.com"
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
              <Label>Anúncio ativo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={uploadingMedia} data-testid="ad-form-submit-button">
                {editingAd ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Delete */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>Tem certeza que deseja excluir este anúncio?</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            A mídia enviada também será removida.
          </p>
          <p className="text-xs text-red-600 mt-2">
            ⚠️ Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvertisementsManagement;
